import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Paper,
  CardHeader,
  Grid,
  Chip,
  DialogTitle,
  IconButton
} from "@mui/material";
import {
  Close as CloseIcon,
  Attachment as AttachmentIcon
} from "@mui/icons-material";
import configAPI from "../../api/config";

const SchoolOnboardingDetailsModal = ({
  open,
  onClose,
  school,
  isMobile
}) => {
  const BASE_URL = configAPI.REACT_APP_API_BASE_URL;

  if (!school) return null;

  const attachments = [
    { label: "School Sign", file: school?.schoolSignUpload },
    { label: "Teacher Document", file: school?.teacherAttachFile },
    { label: "Back Office Staff Document", file: school?.totalBackOfficeAttachFile },
    { label: "Fee Structure", file: school?.feeStructureFile }
  ].filter(item => item.file);

  const handleDownload = async (filePath, label) => {
  try {
    const response = await fetch(`${BASE_URL}${filePath}`);
    const blob = await response.blob();
    const fileExtension = filePath.split('.').pop();

    const fileName = `${label}.${fileExtension}`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    return "";
  }
};


  const SectionWrapper = ({ title, children }) => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "#fafafa",
        // width: "100%"
      }}
    >
      <CardHeader
        sx={{ px: 0, pb: 2 }}
        title={
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
        }
      />
      {children}
    </Paper>
  );

  const Field = ({ label, value }) => (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value || "—"}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "primary.main",
          color: "white"
        }}
      >
        <Typography variant="h6">
          {school?.schoolName || "School Details"}
        </Typography>

        <IconButton size="small" sx={{ color: "white" }} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 3 }}>
    <Box display="flex" flexDirection="column" gap={3}>

    {/*  SCHOOL DETAILS */}
    <SectionWrapper title="School Details">
      <Grid container spacing={1}>
        <Grid item size={{ xs: 12, md: 4 }}>
          <Field label="School Name" value={school?.schoolName} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <Field label="Website" value={school?.websiteUrl} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <Field label="Affiliated To" value={school?.schoolAffiliatedTo} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}} >
          <Field label="School Email" value={school?.schoolEmail} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}} >
          <Field label="School Phone Number" value={school?.schoolPhone} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}} >
          <Field label="School Registration Number" value={school?.schoolRegNo} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <Field label="Address" value={school?.address} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <Field label="State" value={school?.state} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}} >
          <Field label="Country" value={school?.country} />
        </Grid>
      </Grid>
    </SectionWrapper>

    {/* DIRECTOR & ADMIN  */}
    <SectionWrapper title="Director & Admin Details">
      <Grid container spacing={1}>
        {/* Director Row */}
        <Grid item size={{ xs: 12, md: 4}}>
          <Field label="Director Name" value={school?.directorName} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}}>
          <Field label="Director Contact" value={school?.directorContactNumber} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}}>
          <Field label="Director Email" value={school?.directorEmail} />
        </Grid>

        {/* Admin Row */}
        <Grid item size={{ xs: 12, md: 4}}>
          <Field label="Admin Name" value={school?.adminName} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}}>
          <Field label="Admin Contact" value={school?.adminContactNumber} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}}>
          <Field label="Admin Email" value={school?.adminEmail} />
        </Grid>
      </Grid>
    </SectionWrapper>

    {/* ACADEMIC & STAFF */}
    <SectionWrapper title="Academic & Staff Details">
      <Grid container spacing={1}>
        <Grid item size={{ xs: 12, md: 6}}>
          <Field label="Classes" value={school?.classes || school?.className} />
        </Grid>
        <Grid item size={{ xs: 12, md: 6}}>
          <Field label="Student Count" value={school?.studentCount} />
        </Grid>
        <Grid item size={{ xs: 12, md: 6}}>
          <Field label="Total Teachers" value={school?.totalNumberOfTeacher} />
        </Grid>
        <Grid item size={{ xs: 12, md: 6}}>
          <Field label="Back Office Staff" value={school?.totalBackOfficeStaff} />
        </Grid>
      </Grid>
    </SectionWrapper>

    {/*  ATTACHMENTS */}
    <SectionWrapper title="Attachments">
      <Box display="flex" gap={1.5} flexWrap="wrap">
        {attachments.length > 0 ? (
          attachments.map((item, index) => (
            <Chip
             key={index}
            icon={<AttachmentIcon fontSize="small" />}
            label={item.label}
            clickable
            component="a"
            href={`${BASE_URL.replace(/\/$/, "")}/${item.file.replace(/^\//, "")}`}
                download={`${item.label}.${item.file.split('.').pop()}`}
            sx={{ borderRadius: 2 }}
            />
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No attachments available
          </Typography>
        )}
      </Box>
     </SectionWrapper>

     </Box>
    </DialogContent>

    </Dialog>
  );
};

export default SchoolOnboardingDetailsModal;
